/**
 * Default stage/day naming — pure logic.
 *
 * Most multi-day hikes carry auto-generated, POSITIONAL names: a hike day is
 * "Day 3: stage 3" and its route is "stage 3". Those numbers describe where the
 * day sits in the itinerary, so when the itinerary is reversed they have to be
 * renumbered — otherwise the finishing day still reads "Day 1: stage 1".
 *
 * Users can rename both, and a renamed day must never be rewritten. So this is
 * deliberately conservative on two axes:
 *
 *  1. SHAPE — the name has to match one of the known generated templates
 *     exactly. Anything else ("Milngavie to Drymen", "New route", "alt 4") is
 *     left alone.
 *  2. NUMBER — every number in the name must equal the day the name is
 *     currently on. "Day 5: stage 5" sitting on day 3 has been hand-edited into
 *     something we don't understand, so it is left alone too.
 *
 * Renumbering rewrites only the digits, which preserves the original wording and
 * casing — the dominant "Day 1: stage 1" and the rarer "Day 1: Stage 1" both
 * survive as themselves.
 *
 * `alt N` / `spur N` are intentionally NOT handled: their number does not
 * reliably track the day (it is often an offshoot counter), so renumbering them
 * would corrupt names rather than fix them.
 */

/** "Day 3", "Day 3: stage 3", "Day 3: Stage 3" — any casing, tolerant of spacing. */
const DAY_NAME_TEMPLATE = /^\s*day\s+\d+\s*(?::\s*stage\s+\d+\s*)?$/i;

/** "stage 3", "Stage 3", "Day 3" — the generated route titles. */
const ROUTE_NAME_TEMPLATE = /^\s*(?:stage|day)\s+\d+\s*$/i;

function renumberAgainstTemplate(
  name: string | null | undefined,
  template: RegExp,
  fromDay: number,
  toDay: number
): string | null {
  if (!name || !template.test(name)) return null;

  const numbers = name.match(/\d+/g);
  if (!numbers || numbers.length === 0) return null;
  // Every number must be this day's own — otherwise it is hand-edited.
  if (!numbers.every(value => Number(value) === fromDay)) return null;
  if (fromDay === toDay) return null;

  return name.replace(/\d+/g, String(toDay));
}

/**
 * The hike day's new name, or null to leave it exactly as it is.
 */
export function renumberedDayName(
  name: string | null | undefined,
  fromDay: number,
  toDay: number
): string | null {
  return renumberAgainstTemplate(name, DAY_NAME_TEMPLATE, fromDay, toDay);
}

/**
 * The route's new name, or null to leave it exactly as it is.
 */
export function renumberedRouteName(
  name: string | null | undefined,
  fromDay: number,
  toDay: number
): string | null {
  return renumberAgainstTemplate(name, ROUTE_NAME_TEMPLATE, fromDay, toDay);
}

/**
 * Renumber every positional default name to match the day it now sits on.
 *
 * Run AFTER the reversal, so `dayInfo` keys and `route.day` are already the new
 * numbers. `dayMirror` is an involution, so a day's previous number is simply
 * `dayMirror.get(currentDay)` — no need to thread the pre-reversal numbering
 * through.
 */
export function renumberDefaultNamesInPlace(
  state: {
    routes: { [routeKey: string]: { day?: number; name?: string } };
    dayInfo?: { [dayNumber: number]: { name?: string; description?: string; id?: number } };
  },
  dayMirror: Map<number, number>
): void {
  if (dayMirror.size === 0) return;

  const dayInfo = state.dayInfo;
  if (dayInfo) {
    Object.keys(dayInfo).forEach(key => {
      const currentDay = Number(key);
      const previousDay = dayMirror.get(currentDay);
      if (previousDay === undefined) return;
      const renamed = renumberedDayName(dayInfo[currentDay]?.name, previousDay, currentDay);
      if (renamed !== null) dayInfo[currentDay].name = renamed;
    });
  }

  Object.keys(state.routes).forEach(routeKey => {
    const route = state.routes[routeKey];
    if (route.day === undefined) return;
    const previousDay = dayMirror.get(route.day);
    if (previousDay === undefined) return;
    const renamed = renumberedRouteName(route.name, previousDay, route.day);
    if (renamed !== null) route.name = renamed;
  });
}
